// The installable app's shell: which files the service worker (src/sw.ts) keeps for an offline start, how they are
// versioned, and what the page does when a new version shows up. DOM-free so the tests can run it in Node.
//
// Every deploy gets a build id (a hash over the shell files, tools/build.mjs). index.html carries it in
// <meta name="monk-build"> and in the ?v= query of the files it loads; sw.js has it compiled in, so any change to
// the shell changes sw.js and the browser finds a new worker on its next update check.

/** Files referenced with ?v=<build> (their content changes with the build). */
export const VERSIONED_FILES = ['app.js', 'styles.css', 'audio-worklet.js', 'mic-worklet.js', 'monk_web.mjs', 'monk_web.wasm', 'monk_mic.wasm'] as const;
/** Files referenced by their plain name. */
export const PLAIN_FILES = ['favicon.svg', 'manifest.webmanifest', 'icon-192.png', 'icon-512.png'] as const;

export const CACHE_PREFIX = 'monk-shell-';

export function cacheName(build: string): string {
  return CACHE_PREFIX + build;
}

/** `file` with this build's cache-busting query, as the page requests it ("" = no build known: plain name). */
export function versioned(file: string, build: string): string {
  return build ? `${file}?v=${encodeURIComponent(build)}` : file;
}

/** The URLs (relative to the scope) a worker of `build` puts in its cache when it installs. */
export function shellUrls(build: string): string[] {
  return ['./', ...VERSIONED_FILES.map((f) => versioned(f, build)), ...PLAIN_FILES];
}

/** How the worker answers a request: the app page, a shell file, or not at all (the network as usual). */
export type Route = 'page' | 'shell' | 'pass';

export function route(url: URL, scope: URL, method: string, mode: string): Route {
  if (method !== 'GET' || url.origin !== scope.origin || !url.pathname.startsWith(scope.pathname)) return 'pass';
  const rel = url.pathname.slice(scope.pathname.length);
  // Quick Support: the broker API, the landing page (/s/CODE) and the admin page always go to the network untouched
  // (live state, never a cached copy); so does the login page of a locked site.
  if (/^(api|s|admin|support|login)(\/|$)/.test(rel)) return 'pass';
  if (mode === 'navigate') return rel === '' || rel === 'index.html' ? 'page' : 'pass';
  return (VERSIONED_FILES as readonly string[]).includes(rel) || (PLAIN_FILES as readonly string[]).includes(rel) ? 'shell' : 'pass';
}

/** A locked site's login page that comes back to `loc` (path, query and #-route) after the login. */
export function loginUrl(loc: { pathname: string; search: string; hash: string }): string {
  const back = loc.pathname + loc.search + loc.hash;
  return back === '/' ? '/login' : `/login?next=${encodeURIComponent(back)}`;
}

/** Where the login page goes afterwards: its ?next= when that is a page of this site, else the web client. */
export function loginNext(search: string, hash: string): string {
  const n = new URLSearchParams(search).get('next') ?? '';
  const ok = n.startsWith('/') && !n.startsWith('//') && !n.startsWith('/\\') && !/^\/login(\/|\?|#|$)/.test(n) && !/[\x00-\x1f]/.test(n);
  const to = ok ? n : '/';
  return to.includes('#') || !hash ? to : to + hash;
}

/** Caches an activated worker of `build` deletes: older shells (other apps' caches on the origin are left alone). */
export function staleCaches(names: readonly string[], build: string): string[] {
  return names.filter((n) => n.startsWith(CACHE_PREFIX) && n !== cacheName(build));
}

/**
 * A new worker finished installing. `activate`: take over quietly (the first install, or this page already is that
 * version: the network-first page load got the new files). `prompt`: this page runs an older build: offer a reload.
 */
export function updateAction(hadController: boolean, workerBuild: string | null, pageBuild: string): 'activate' | 'prompt' {
  if (!hadController) return 'activate';
  if (workerBuild !== null && workerBuild === pageBuild) return 'activate';
  return 'prompt';
}

/** Messages between the page and the worker. */
export type ShellMsg = { type: 'version' } | { type: 'skip-waiting' };
